<template>
    <tr :class="`${isEditRequested || isDeleteRequested || isAddRequested ? 'edit-row' : ''}`">
        <td class="align-top">
            <div class="form-group mb-0" v-if="isEditRequested || isAddRequested">
                <TypeaheadInput
                    :getData="getProblemsList"
                    :value="selectedProblem"
                    placeholder="Search for Problem"
                    :init-value="selectedProblem"
                    v-on:valueChanged="handleProblemChange"
                />
            </div>
            <span v-else>
                <EmptyTextHandler :text="problem.description" />
            </span>
        </td>
        <td class="align-top">
            <template v-if="isEditRequested || isAddRequested">
                <div class="form-group mb-0" v-if="!problem.isStartDateUnknown">
                    <DatePicker :getDate="handleDateChange">
                        <div class="input-group date datepickerInput">
                            <input
                                name="startDate"
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                :value="formatDateInput(problem.startDate)"
                                autocomplete="off"
                                @change="handleDateInputChange"
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <div class="form-group mb-0" v-else>
                    <DatePicker>
                        <div class="input-group date datepickerInput">
                            <input
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                autocomplete="off"
                                disabled
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <div class="form-check pt-0 ps-4 form-check-inline">
                    <input
                        type="checkbox"
                        class="form-check-input au-target"
                        :id="`start-${currentView}-${index}`"
                        v-model="problem.isStartDateUnknown"
                    />
                    <label class="form-check-label" :for="`start-${currentView}-${index}`">
                        <span class="me-1">Unknown</span>
                    </label>
                </div>
            </template>
            <template v-else>
                <span v-if="problem.isStartDateUnknown">Unknown</span>
                <EmptyTextHandler :text="formatDate(problem.startDate)" v-else />
            </template>
        </td>
        <td class="align-top">
            <template v-if="isEditRequested || isAddRequested">
                <div class="form-group mb-0" v-if="!problem.isResolvedDateUnknown">
                    <DatePicker :getDate="handleDateChange">
                        <div class="input-group date datepickerInput">
                            <input
                                name="resolvedDate"
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                :value="formatDateInput(problem.resolvedDate)"
                                autocomplete="off"
                                @change="handleDateInputChange"
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <div class="form-group mb-0" v-else>
                    <DatePicker>
                        <div class="input-group date datepickerInput">
                            <input
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                autocomplete="off"
                                disabled
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <div class="form-check pt-0 ps-4 form-check-inline">
                    <input
                        type="checkbox"
                        class="form-check-input au-target"
                        :id="`resolve-${currentView}-${index}`"
                        v-model="problem.isResolvedDateUnknown"
                    />
                    <label class="form-check-label" :for="`resolve-${currentView}-${index}`">
                        <span class="me-1">Unknown</span>
                    </label>
                </div>
            </template>
            <template v-else>
                <span v-if="problem.isResolvedDateUnknown">Unknown</span>
                <EmptyTextHandler :text="formatDate(problem.resolvedDate)" v-else />
            </template>
        </td>
        <td class="text-end" v-if="!isEditRequested && !isDeleteRequested && !isAddRequested">
            <button class="btn table-btn btn-outline-info" @click="openEditProblem()">
                <i class="fas fa-edit"></i>
            </button>
            <button class="btn table-btn btn-outline-danger" @click="openDeleteProblem()">
                <i class="fas fa-trash-alt"></i>
            </button>
            <div class="btn-group table-btn-group" v-if="problemsLength > 1">
                <button
                    type="button"
                    class="btn px-1 py-0 btn-outline-primary dropdown-toggle"
                    data-bs-toggle="dropdown"
                    :id="`ProbelmsRow${problem.resolvedOrder}`"
                >
                    <i class="fas fa-ellipsis-h"></i>
                </button>
                <div class="dropdown-menu" :aria-labelledby="`ProbelmsRow${problem.resolvedOrder}`">
                    <a
                        class="dropdown-item"
                        href="javascript:void(0);"
                        @click="moveProblemUp(index, index - 1)"
                        v-if="!first"
                    >
                        Move up
                    </a>
                    <a
                        class="dropdown-item"
                        href="javascript:void(0);"
                        @click="moveProblemDown(index, index + 1)"
                        v-if="!last"
                    >
                        Move down
                    </a>
                </div>
            </div>
        </td>
        <td :colspan="columns" class="p-2 text-end" v-if="isDeleteRequested">
            <TablePrompt
                ok-text="Delete"
                name="delete"
                :ok="handleDeleteProblem"
                :cancel="cancelDeleteRequest"
                :message="deleteMessage"
                cancel-text="No"
                :include-yes="false"
            />
        </td>
        <td class="button-cell text-end" v-if="isEditRequested">
            <button type="button" class="btn table-btn btn-outline-success" @click="saveEditProblem()">Add</button>
            <button type="button" class="btn table-btn btn-outline-secondary" @click="cancelEditProblem()">
                Cancel
            </button>
        </td>
        <td class="button-cell text-end" v-if="isAddRequested">
            <button type="button" class="btn table-btn btn-outline-success" @click="save()">Add</button>
            <button type="button" class="btn table-btn btn-outline-secondary" @click="cancelSaveNewProblem()">
                Cancel
            </button>
        </td>
    </tr>
</template>

<script lang="ts" src="./ProblemsRow.ts" />
